import { CSSProperties } from 'react';

import FrameSection from '../FrameSection';
import Skill from './Skill';
import AbilityScoreBadge from './AbilityScoreBadge';

export type ProficiencyLevel = 0 | 1 | 2;

export type AbilitySkill = {
    name: string;
    proficiency: ProficiencyLevel;
};

type AbilityProps = {
    name: string;
    score: number;
    level: number;
    savingThrowProficiency?: ProficiencyLevel;
    skills?: AbilitySkill[];
    disabled?: boolean;
    onScoreChange?: (score: number) => void;
    onSavingThrowProficiencyChange?: (proficiency: ProficiencyLevel) => void;
    onSkillProficiencyChange?: (skillName: string, proficiency: ProficiencyLevel) => void;
    style?: CSSProperties;
};

function formatModifier(value: number) {
    return value >= 0 ? `+${value}` : `${value}`;
}

export function getAbilityModifier(score: number) {
    return Math.floor((score - 10) / 2);
}

export function getProficiencyBonus(level: number) {
    const normalizedLevel = Math.max(1, Math.min(20, Math.floor(level)));
    return 2 + Math.floor((normalizedLevel - 1) / 4);
}

function Ability({
    name,
    score,
    level,
    savingThrowProficiency = 0,
    skills = [],
    onScoreChange,
    onSavingThrowProficiencyChange,
    onSkillProficiencyChange,
    style = {},
}: AbilityProps) {
    const modifier = getAbilityModifier(score);
    const proficiencyBonus = getProficiencyBonus(level);
    const savingThrowModifier = modifier + savingThrowProficiency * proficiencyBonus;

    return (
        <div style={{
            width: '100%',
            minWidth: 0,
            padding: '.15em',
            color: '#4b4b49',
            ...style,
        }}>
            <FrameSection title={name} style={{ fontSize: 12 }} />

            <div style={{ marginBottom: '.1rem' }}>
                <AbilityScoreBadge
                    name={name}
                    score={score}
                    modifier={formatModifier(modifier)}
                    onScoreChange={onScoreChange}
                />
            </div>

            <Skill
                name='Saving Throw'
                modifier={savingThrowModifier}
                proficiency={savingThrowProficiency}
                disabled={!onSavingThrowProficiencyChange}
                onProficiencyChange={(value) => onSavingThrowProficiencyChange?.(value as ProficiencyLevel)}
            />


            {skills.length > 0 && (
                <>
                    <hr style={{ margin: '.2rem 0' }} />

                    <div className='d-flex flex-column'>
                        {skills.map((skill) => (
                            <Skill
                                key={skill.name}
                                name={skill.name}
                                modifier={modifier + skill.proficiency * proficiencyBonus}
                                proficiency={skill.proficiency}
                                disabled={!onSkillProficiencyChange}
                                onProficiencyChange={(value) => onSkillProficiencyChange?.(skill.name, value as ProficiencyLevel)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default Ability;
